import { noop, type QueryClient } from '@tanstack/react-query';
import type { LoaderFunctionArgs } from 'react-router';
import { trackedMetrics } from '@/lib/body-metrics';
import { isCoached } from '@/lib/client-state';
import { activityOptions } from '@/services/activity';
import { measurementsOptions, photosOptions } from '@/services/body';
import { clientOptions } from '@/services/clients';
import { notesOptions } from '@/services/notes';
import {
  currentProgramOptions,
  programHistoryOptions,
  programOptions,
} from '@/services/programs';
import {
  adherenceOptions,
  statsOptions,
  weeklyTotalsOptions,
} from '@/services/stats';
import {
  lastSessionOptions,
  sessionsOptions,
  trainingDaysOptions,
} from '@/services/workouts';
import { queryClientContext } from './query-client-context';

type ClientTab =
  | 'overview'
  | 'program'
  | 'statistics'
  | 'body'
  | 'activity'
  | 'settings';

type Start = (queryClient: QueryClient, clientId: string) => void;

type TabReads = {
  /** The coach's own records: readable in every state of the client. */
  own?: Start;
  /** The athlete's records: asked for only while coaching is open (PD-008). */
  athlete?: Start;
};

// The program the client is on, in two reads: which one, then its plan.
const currentProgram: Start = (queryClient, clientId) => {
  void queryClient
    .query(currentProgramOptions(clientId))
    .then(({ items: [program] }) =>
      program ? queryClient.query(programOptions(clientId, program.id)) : null,
    )
    .catch(noop);
};

// The Body tab's measurements, in two steps: body weight, which says what the
// client tracks, then each tracked measurement for its latest value.
const measurements: Start = (queryClient, clientId) => {
  void queryClient
    .query(measurementsOptions(clientId))
    .then(({ availableMetrics }) =>
      Promise.all(
        trackedMetrics(availableMetrics).map((metric) =>
          queryClient.query(measurementsOptions(clientId, metric)),
        ),
      ),
    )
    .catch(noop);
};

/**
 * What each tab of the client page reads with its default parameters
 * (ADR 0016): the one place a tab's reads are listed.
 */
const tabReads: Record<ClientTab, TabReads> = {
  overview: {
    own: (queryClient, clientId) => {
      currentProgram(queryClient, clientId);
      void queryClient.query(notesOptions(clientId)).catch(noop);
      void queryClient.infiniteQuery(activityOptions(clientId)).catch(noop);
    },
    athlete: (queryClient, clientId) => {
      void queryClient.query(adherenceOptions(clientId)).catch(noop);
      void queryClient.query(weeklyTotalsOptions(clientId)).catch(noop);
      void queryClient.query(trainingDaysOptions(clientId)).catch(noop);
      void queryClient.query(measurementsOptions(clientId)).catch(noop);
      void queryClient.query(photosOptions(clientId)).catch(noop);
    },
  },
  program: {
    own: (queryClient, clientId) => {
      currentProgram(queryClient, clientId);
      void queryClient.query(programHistoryOptions(clientId)).catch(noop);
    },
    athlete: (queryClient, clientId) => {
      void queryClient.infiniteQuery(sessionsOptions(clientId)).catch(noop);
    },
  },
  statistics: {
    athlete: (queryClient, clientId) => {
      void queryClient.query(statsOptions(clientId)).catch(noop);
    },
  },
  body: {
    athlete: (queryClient, clientId) => {
      measurements(queryClient, clientId);
      void queryClient.query(photosOptions(clientId)).catch(noop);
    },
  },
  activity: {
    own: (queryClient, clientId) => {
      void queryClient.infiniteQuery(activityOptions(clientId)).catch(noop);
    },
  },
  settings: {
    own: (queryClient, clientId) => {
      void queryClient.query(notesOptions(clientId)).catch(noop);
    },
  },
};

function clientIdOf({ params }: LoaderFunctionArgs) {
  const { clientId } = params;
  if (!clientId) throw new Error('clientId is required');
  return clientId;
}

/** The client, as the page frame needs it; every caller shares one request. */
function readClient(queryClient: QueryClient, clientId: string) {
  return queryClient.query({ ...clientOptions(clientId), staleTime: 'static' });
}

// Starts the tabs' reads without waiting for any. The athlete's records wait
// for the client alone, to know that coaching is still open.
function start(tabs: TabReads[], queryClient: QueryClient, clientId: string) {
  for (const { own } of tabs) own?.(queryClient, clientId);
  void readClient(queryClient, clientId)
    .then(({ state }) => {
      if (!isCoached(state)) return;
      for (const { athlete } of tabs) athlete?.(queryClient, clientId);
    })
    .catch(noop);
}

/** Entering a tab: its own reads start, never awaited. */
export const tabLoader = (tab: ClientTab) => (args: LoaderFunctionArgs) => {
  start(
    [tabReads[tab]],
    args.context.get(queryClientContext),
    clientIdOf(args),
  );
  return null;
};

/**
 * Entering `/clients/:clientId`: every tab's reads start, and the navigation
 * waits for the client alone, the page frame. A client that cannot be read
 * fails the navigation into the route's own error element.
 */
export async function clientLoader(args: LoaderFunctionArgs) {
  const queryClient = args.context.get(queryClientContext);
  const clientId = clientIdOf(args);
  start(Object.values(tabReads), queryClient, clientId);
  const client = await readClient(queryClient, clientId);
  // The header's line is the athlete's record too.
  if (isCoached(client.state)) {
    void queryClient.query(lastSessionOptions(clientId)).catch(noop);
  }
  return null;
}
